import React, { useState } from 'react';
import { ThumbsUp, ThumbsDown, Check } from 'lucide-react';
import { apiService } from '../../services/apiService';

interface Grade { criterion: string; score: number; maxScore: number; feedback?: string; }

interface FeedbackWidgetProps {
  result: {
    studentId: number;
    transcription?: string;
    grades?: Grade[];
    criteria?: Grade[];
  };
  rubricCriteria: Array<{ name: string; description?: string; maxScore: number }>;
  /** Original AI scores, captured before any teacher edit. Falls back to current grades. */
  aiGrades?: Array<{ criterion: string; score: number; maxScore: number }>;
}

export const FeedbackWidget: React.FC<FeedbackWidgetProps> = ({ result, rubricCriteria, aiGrades }) => {
  const [rating, setRating] = useState<'good' | 'bad' | null>(null);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!rating) { setError('Please rate this grade first'); return; }
    setSaving(true);
    setError(null);
    try {
      const finalGrades = (result.grades || result.criteria || []).map(g => ({
        criterion: g.criterion, score: g.score, maxScore: g.maxScore,
      }));
      const res = await apiService.submitFeedback({
        transcription: result.transcription || '',
        rubricSnapshot: rubricCriteria,
        aiGrades: aiGrades || finalGrades,
        finalGrades,
        rating,
        reason: reason.trim() || undefined,
      });
      setSaving(false);
      if (res && res.success) { setSaved(true); } else { setError((res && res.error) || 'Failed to save'); }
    } catch (err: any) {
      setSaving(false);
      setError(err?.message || 'Failed to save');
    }
  };

  if (saved) {
    return (
      <div className="mt-3 flex items-center text-sm text-green-700">
        <Check className="h-4 w-4 mr-1" /> Thanks — MarkerMate will use this to grade more like you.
      </div>
    );
  }

  return (
    <div className="mt-3 border-t border-gray-100 pt-3">
      <p className="text-sm font-medium text-gray-800 mb-2">Was this grade right?</p>
      <div className="flex items-center space-x-2 mb-2">
        <button
          type="button"
          aria-label="Looks good"
          aria-pressed={rating === 'good'}
          onClick={() => setRating('good')}
          className={`flex items-center px-3 py-1 rounded border text-sm ${rating === 'good' ? 'bg-green-50 border-green-400 text-green-700' : 'border-gray-300 text-gray-600'}`}
        >
          <ThumbsUp className="h-4 w-4 mr-1" /> Looks good
        </button>
        <button
          type="button"
          aria-label="Not quite"
          aria-pressed={rating === 'bad'}
          onClick={() => setRating('bad')}
          className={`flex items-center px-3 py-1 rounded border text-sm ${rating === 'bad' ? 'bg-red-50 border-red-400 text-red-700' : 'border-gray-300 text-gray-600'}`}
        >
          <ThumbsDown className="h-4 w-4 mr-1" /> Not quite
        </button>
      </div>
      <textarea
        value={reason}
        onChange={e => setReason(e.target.value)}
        aria-label="Reason for rating"
        placeholder="Why? (e.g. what should be different about this grade)"
        className="w-full text-sm border border-gray-300 rounded p-2 mb-2"
        rows={2}
      />
      {error && <p className="text-sm text-red-600 mb-2">{error}</p>}
      <button
        type="button"
        onClick={submit}
        disabled={saving}
        className="btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {saving ? 'Saving…' : 'Save feedback'}
      </button>
    </div>
  );
};
